import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useAccesos } from '../contexts/AccesosContext';

const ReporteFacturas = () => {
  const server = process.env.REACT_APP_API_ROUTE;
  const { tienePermiso } = useAccesos();

  // Estados de datos
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sucursales, setSucursales] = useState([]);

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(() => {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - 30);
    return fecha.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [idSucursal, setIdSucursal] = useState('');

  // Tabla
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('idFactura');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    cargarSucursales();
  }, []);

  const cargarSucursales = async () => {
    try {
      const response = await fetch(`${server}/MgSucursales`);
      if (response.ok) {
        const data = await response.json();
        let arr = Array.isArray(data) ? data : data?.$values || [];
        setSucursales(arr);
      }
    } catch (err) {
      console.error('Error al cargar sucursales:', err);
    }
  };

  const handleBuscar = async () => {
    if (fechaInicio && fechaFin && new Date(fechaInicio) > new Date(fechaFin)) {
      alert('La fecha fin no puede ser menor a la fecha inicio');
      return;
    }

    setLoading(true);
    setError(null);
    setPage(0);

    try {
      const sucId = idSucursal || 0;
      const f1 = fechaInicio.replace(/-/g, '');
      const f2 = fechaFin.replace(/-/g, '');
      const response = await fetch(
        `${server}/MgCajaArqueoDetalleSistemas/reporteFacturas/${sucId}/${f1}/${f2}`
      );

      if (response.ok) {
        const data = await response.json();
        let arr = Array.isArray(data) ? data : data?.$values || [];
        setFacturas(arr);
      } else if (response.status === 404) {
        setFacturas([]);
        setError('No se encontraron facturas para los filtros seleccionados.');
      } else {
        setError('Error al obtener los datos.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error de conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    const fechaFinNueva = new Date();
    const fechaInicioNueva = new Date();
    fechaInicioNueva.setDate(fechaInicioNueva.getDate() - 30);

    setFechaInicio(fechaInicioNueva.toISOString().split('T')[0]);
    setFechaFin(fechaFinNueva.toISOString().split('T')[0]);
    setIdSucursal('');
    setFacturas([]);
    setError(null);
  };

  // Ordenamiento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!facturas || facturas.length === 0) return [];
    return [...facturas].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [facturas, order, orderBy]);

  const paginatedData = useMemo(() => {
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  // Totales
  const totales = useMemo(() => {
    if (!facturas || facturas.length === 0)
      return { valorVenta: 0, valorDescuento: 0, lempirasLacm: 0, granTotal: 0 };

    return facturas.reduce(
      (acc, row) => ({
        valorVenta: acc.valorVenta + (row.valorVenta || 0),
        valorDescuento: acc.valorDescuento + (row.valorDescuento || 0),
        lempirasLacm: acc.lempirasLacm + (row.lempirasLacm || 0),
        granTotal: acc.granTotal + (row.granTotal || 0)
      }),
      { valorVenta: 0, valorDescuento: 0, lempirasLacm: 0, granTotal: 0 }
    );
  }, [facturas]);

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('es-HN', {
      style: 'currency',
      currency: 'HNL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (date.getFullYear() <= 1900) return '';
    return date.toLocaleDateString('es-HN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatPercent = (value) => {
    if (!value) return '0.00%';
    return (value * 100).toFixed(2) + '%';
  };

  // Exportar Excel
  const exportarExcel = () => {
    const datosExportar = facturas.map((row) => ({
      'ID Factura': row.idFactura,
      'Fecha': formatDate(row.fechaFactura),
      'No. Factura': row.numeroFactura,
      'Órdenes': row.ordenes,
      'Identidad/RTN': row.identidadRtn,
      'Cliente': row.cliente,
      'Médico': row.nombreMedico,
      'Tipo Cliente': row.tipoCliente,
      'Forma Pago': row.formaPago,
      'Sucursal': row.nombreSucursal,
      'Ingresado Por': row.ingresadoPor,
      'Valor Venta': row.valorVenta,
      'Descuento': row.valorDescuento,
      '% Descuento': row.porcentajeDescuento,
      'Puntos LACM': row.lempirasLacm,
      'Gran Total': row.granTotal
    }));

    datosExportar.push({
      'ID Factura': '',
      'Fecha': '',
      'No. Factura': 'TOTALES',
      'Órdenes': '',
      'Identidad/RTN': '',
      'Cliente': '',
      'Médico': '',
      'Tipo Cliente': '',
      'Forma Pago': '',
      'Sucursal': '',
      'Ingresado Por': '',
      'Valor Venta': totales.valorVenta,
      'Descuento': totales.valorDescuento,
      '% Descuento': '',
      'Puntos LACM': totales.lempirasLacm,
      'Gran Total': totales.granTotal
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(datosExportar);

    ws['!cols'] = [
      { wch: 10 },  // ID
      { wch: 12 },  // Fecha
      { wch: 20 },  // No. Factura
      { wch: 25 },  // Órdenes
      { wch: 18 },  // Identidad
      { wch: 30 },  // Cliente
      { wch: 25 },  // Médico
      { wch: 15 },  // Tipo Cliente
      { wch: 15 },  // Forma Pago
      { wch: 25 },  // Sucursal
      { wch: 15 },  // Ingresado Por
      { wch: 14 },  // Valor Venta
      { wch: 14 },  // Descuento
      { wch: 12 },  // % Descuento
      { wch: 14 },  // Puntos LACM
      { wch: 14 }   // Gran Total
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Reporte Facturas');
    XLSX.writeFile(wb, `ReporteFacturas_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const columnas = [
    { id: 'idFactura', label: 'ID', minWidth: 60, align: 'center' },
    { id: 'fechaFactura', label: 'Fecha', minWidth: 100 },
    { id: 'numeroFactura', label: 'No. Factura', minWidth: 150 },
    { id: 'ordenes', label: 'Órdenes', minWidth: 160 },
    { id: 'identidadRtn', label: 'Identidad/RTN', minWidth: 140 },
    { id: 'cliente', label: 'Cliente', minWidth: 200 },
    { id: 'nombreMedico', label: 'Médico', minWidth: 180 },
    { id: 'tipoCliente', label: 'Tipo Cliente', minWidth: 110 },
    { id: 'formaPago', label: 'Forma Pago', minWidth: 120 },
    { id: 'nombreSucursal', label: 'Sucursal', minWidth: 150 },
    { id: 'ingresadoPor', label: 'Ingresado Por', minWidth: 120 },
    { id: 'valorVenta', label: 'Valor Venta', minWidth: 120, align: 'right' },
    { id: 'valorDescuento', label: 'Descuento', minWidth: 110, align: 'right' },
    { id: 'porcentajeDescuento', label: '% Desc.', minWidth: 80, align: 'right' },
    { id: 'lempirasLacm', label: 'Puntos LACM', minWidth: 110, align: 'right' },
    { id: 'granTotal', label: 'Gran Total', minWidth: 120, align: 'right' }
  ];

  return (
    <div className="right_col" role="main" style={{ minHeight: '100vh' }}>
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>Reporte de Facturas</h3>
          </div>
        </div>
        <div className="clearfix"></div>

        <div className="row">
          <div className="col-md-12 col-sm-12">
            <Paper sx={{ p: 3 }}>

              {/* Filtros */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Fecha Inicio"
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon />
                          </InputAdornment>
                        )
                      }}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Fecha Fin"
                      type="date"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon />
                          </InputAdornment>
                        )
                      }}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth disabled={loading}>
                      <InputLabel id="sucursal-label">Sucursal</InputLabel>
                      <Select
                        labelId="sucursal-label"
                        value={idSucursal || ''}
                        label="Sucursal"
                        onChange={(e) => setIdSucursal(e.target.value)}
                        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                      >
                        <MenuItem value="">
                          <em>Todas las sucursales</em>
                        </MenuItem>
                        {sucursales.map((s) => (
                          <MenuItem key={s.idSucursal} value={s.idSucursal}>
                            {s.nombreSucursal}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon />}
                        onClick={handleBuscar}
                        disabled={loading || !tienePermiso('Buscar Reporte Facturas')}
                      >
                        Buscar
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon />}
                        onClick={handleLimpiar}
                        disabled={loading}
                      >
                        Limpiar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Loading */}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {/* Error */}
              {error && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Tabla */}
              {!loading && facturas.length > 0 && (
                <>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1">
                      {facturas.length} factura(s) encontrada(s)
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<GetAppIcon />}
                      onClick={exportarExcel}
                      disabled={!tienePermiso('Exportar Excel Facturas')}
                    >
                      Exportar a Excel
                    </Button>
                  </Box>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {columnas.map((col) => (
                            <TableCell
                              key={col.id}
                              align={col.align || 'left'}
                              style={{ minWidth: col.minWidth, fontWeight: 'bold' }}
                            >
                              <TableSortLabel
                                active={orderBy === col.id}
                                direction={orderBy === col.id ? order : 'asc'}
                                onClick={() => handleRequestSort(col.id)}
                              >
                                {col.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedData.map((row, index) => (
                          <TableRow hover key={row.idFactura || index}>
                            <TableCell align="center">{row.idFactura}</TableCell>
                            <TableCell>{formatDate(row.fechaFactura)}</TableCell>
                            <TableCell>{row.numeroFactura}</TableCell>
                            <TableCell>{row.ordenes || '-'}</TableCell>
                            <TableCell>{row.identidadRtn}</TableCell>
                            <TableCell>{row.cliente}</TableCell>
                            <TableCell>{row.nombreMedico || '-'}</TableCell>
                            <TableCell>{row.tipoCliente}</TableCell>
                            <TableCell>{row.formaPago}</TableCell>
                            <TableCell>{row.nombreSucursal}</TableCell>
                            <TableCell>{row.ingresadoPor}</TableCell>
                            <TableCell align="right">{formatNumber(row.valorVenta)}</TableCell>
                            <TableCell align="right">{formatNumber(row.valorDescuento)}</TableCell>
                            <TableCell align="right">{formatPercent(row.porcentajeDescuento)}</TableCell>
                            <TableCell align="right">{formatNumber(row.lempirasLacm)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {formatNumber(row.granTotal)}
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Fila de totales */}
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell colSpan={11} sx={{ fontWeight: 'bold' }}>TOTALES</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatNumber(totales.valorVenta)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatNumber(totales.valorDescuento)}
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatNumber(totales.lempirasLacm)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatNumber(totales.granTotal)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={sortedData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  />
                </>
              )}

              {/* Estado vacío inicial */}
              {!loading && !error && facturas.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <Typography variant="h6">
                    Seleccione los filtros y presione Buscar
                  </Typography>
                </Box>
              )}
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReporteFacturas;
